import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import {
  setIncomeStatement,
  setReportLoading,
  setReportError,
  setPrintData
} from '../../redux/accounting';

const IncomeStatement = () => {
  const dispatch = useDispatch();
  const { incomeStatement, reportsLoading, reportsError } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const loadIncomeStatement = useCallback(async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setReportLoading({ reportType: 'incomeStatement', loading: true }));
    
    try {
      // Generate income statement from real data
      const data = await realDataAccountingService.generateRealIncomeStatement(
        currentSchool?.schoolID,
        startDate,
        endDate,
        reduxData
      );
      dispatch(setIncomeStatement(data));
    } catch (err) {
      setError('Failed to load income statement');
      dispatch(setReportError({ reportType: 'incomeStatement', error: err.message }));
      console.error('Income statement error:', err);
    } finally {
      setLoading(false);
      dispatch(setReportLoading({ reportType: 'incomeStatement', loading: false }));
    }
  }, [currentSchool?.schoolID, startDate, endDate, reduxData, dispatch]);

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadIncomeStatement();
    }
  }, [currentSchool, startDate, endDate, loadIncomeStatement]);

  const handleRefresh = () => {
    loadIncomeStatement();
  };

  const handlePrint = () => {
    dispatch(setPrintData({ reportType: 'incomeStatement', data: incomeStatement }));
    // Print logic would be implemented here
  };

  const handleExport = async (format = 'pdf') => {
    try {
      const data = await accountingService.exportReport('income-statement', incomeStatement, format);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `income_statement_${startDate}_to_${endDate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const formatCurrency = (amount) => accountingService.formatCurrency(amount);

  const getNetIncomeColor = (netIncome) => {
    return netIncome >= 0 ? '#388e3c' : '#d32f2f';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading income statement...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Income Statement
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!incomeStatement}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!incomeStatement}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!incomeStatement}
                >
                  Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Income Statement */}
      {incomeStatement && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" align="center">
              {currentSchool?.schoolname || 'School'} Income Statement
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              For the period {accountingService.formatDate(startDate)} to {accountingService.formatDate(endDate)}
            </Typography>
          </Box>
          
          <Box sx={{ p: 2 }}>
            {/* Revenue Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#388e3c' }}>
                REVENUE
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>School Fees</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.revenue?.schoolFees || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Canteen Fees</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.revenue?.canteenFees || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Sales Revenue</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.revenue?.salesRevenue || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Other Revenue</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.revenue?.otherRevenue || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Revenue</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(incomeStatement.revenue?.totalRevenue || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Expenses Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
                EXPENSES
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Salaries and Wages</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.salaries || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Operating Expenses</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.operatingExpenses || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Administrative Expenses</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.administrativeExpenses || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Utilities</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.utilities || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Maintenance and Repairs</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.maintenance || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Professional Services</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.professionalServices || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Other Expenses</TableCell>
                      <TableCell align="right">
                        {formatCurrency(incomeStatement.expenses?.otherExpenses || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Expenses</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(incomeStatement.expenses?.totalExpenses || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Net Income Section */}
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ 
                      borderTop: 3, 
                      borderColor: 'primary.main', 
                      backgroundColor: '#f5f5f5' 
                    }}>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.1rem',
                        color: getNetIncomeColor(incomeStatement.netIncome || 0)
                      }}>
                        NET INCOME
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.1rem',
                        color: getNetIncomeColor(incomeStatement.netIncome || 0)
                      }}>
                        {formatCurrency(incomeStatement.netIncome || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Net Income Analysis */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Financial Analysis
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Revenue Growth: {incomeStatement.revenue?.growthRate || 0}%
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Expense Ratio: {incomeStatement.expenses?.expenseRatio || 0}%
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Profit Margin: {incomeStatement.profitMargin || 0}%
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Operating Efficiency: {incomeStatement.operatingEfficiency || 0}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      )}

      {!incomeStatement && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <TrendingUpIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No income statement data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a date range and click refresh to generate income statement
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default IncomeStatement;
